/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useEffect, useMemo } from 'react'
// Third-party
import { css } from '@emotion/react'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core'
import type {
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
// Relative
import {
  heading4,
  paragraphSmall,
  paragraphXSmall,
  paragraphMedium,
  weightSemiBold,
  weightMedium,
  weightRegular,
  weightBold,
} from 'src/components/css'
import {
  CheckboxOutlineIcon,
  CheckboxCheckedIcon,
  CheckboxDisabledIcon,
} from './icons'

export type DivisionScheduleStatus = 'pending' | 'generated' | 'approved'

export interface DivisionLocation {
  id: string
  name: string
  surfaceCount: number
}

export interface Division {
  id: string
  name: string
  locations: DivisionLocation[] | 'all'
  status: string
  scheduleStatus?: DivisionScheduleStatus
  isDisabled?: boolean
  isApproved?: boolean
  lastUpdatedAt?: string
  lastUpdatedBy?: string
}

interface GenerateScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (
    selectedDivisions: string[],
    selectedLocations: string[],
    divisionPriority: string[]
  ) => void
  onResetDivisions?: (divisionIds: string[]) => Promise<boolean>
  divisions: Division[]
  previewPayload?: Record<string, unknown> | null
  isLoadingPreview?: boolean
}

/**
 * Status badge for division schedule status
 */
const StatusBadge: React.FC<{
  status?: DivisionScheduleStatus
  isApproved?: boolean
}> = ({ status, isApproved }) => {
  let bgColor: string
  let textColor: string
  let label: string

  if (isApproved) {
    bgColor = `${colors.SUCCESS_300}20`
    textColor = colors.SUCCESS_300
    label = 'Approved'
  } else {
    switch (status) {
      case 'approved':
        bgColor = `${colors.SUCCESS_300}20`
        textColor = colors.SUCCESS_300
        label = 'Approved'
        break
      case 'generated':
        bgColor = `${colors.INFO_300}20`
        textColor = colors.INFO_300
        label = 'Generated'
        break
      case 'pending':
      default:
        bgColor = `${colors.WARNING_300}20`
        textColor = colors.WARNING_300
        label = 'Pending'
        break
    }
  }

  return (
    <span
      css={css`
        display: inline-block;
        padding: 2px 8px;
        border-radius: 12px;
        background: ${bgColor};
        color: ${textColor};
        font-family: 'Inter', sans-serif;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
      `}
    >
      {label}
    </span>
  )
}

/**
 * Grip/drag handle icon (6-dot pattern)
 */
const GripIcon: React.FC<{ color?: string }> = ({
  color = colors.NEUTRAL_100,
}) => (
  <svg width='10' height='16' viewBox='0 0 10 16' fill='none'>
    <circle cx='2' cy='2' r='1.5' fill={color} />
    <circle cx='8' cy='2' r='1.5' fill={color} />
    <circle cx='2' cy='8' r='1.5' fill={color} />
    <circle cx='8' cy='8' r='1.5' fill={color} />
    <circle cx='2' cy='14' r='1.5' fill={color} />
    <circle cx='8' cy='14' r='1.5' fill={color} />
  </svg>
)

const priorityColumnStyles = css`
  width: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`

const dragHandleStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  padding: 4px 2px;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
  }

  &:active {
    cursor: grabbing;
  }
`

const priorityBadgeStyles = css`
  ${paragraphXSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_70};
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${colors.SECONDARY_200};
  font-size: 10px;
`


const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const titleStyles = css`
  ${heading4}
  ${weightSemiBold}
  color: ${colors.WHITE};
  margin: 0;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const sectionHeaderStyles = css`
  display: flex;
  flex-direction: column;
`

const sectionTitleStyles = css`
  ${paragraphMedium}
  ${weightBold}
  color: ${colors.WHITE};
  margin: 0;
`

const sectionSubtitleStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const tableContainerStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const tableHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  background-color: ${colors.SECONDARY_100};
`

const tableHeaderCellStyles = css`
  ${paragraphXSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_70};
  flex: 1;
  text-transform: uppercase;
`


const divisionRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_500};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.SECONDARY_400};
  }
`

const divisionRowSelectedStyles = css`
  background-color: rgba(56, 148, 255, 0.08);

  &:hover {
    background-color: rgba(56, 148, 255, 0.12);
  }
`

const divisionRowDisabledStyles = css`
  background-color: ${colors.SECONDARY_500};
  cursor: default;

  &:hover {
    background-color: ${colors.SECONDARY_500};
  }
`

const altRowStyles = css`
  background-color: ${colors.SECONDARY_400};

  &:hover {
    background-color: ${colors.SECONDARY_300};
  }
`

const divisionNameStyles = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const locationCellStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const statusCellStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`


const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
`

const buttonContainerStyles = css`
  display: flex;
  gap: 16px;
  width: 360px;
`

const cancelButtonStyles = css`
  flex: 1;
  padding: 9px 25px;
  border-radius: 8px;
  border: 1px solid ${colors.WHITE};
  background: transparent;
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const confirmButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_400};
  }

  &:disabled {
    background: ${colors.NEUTRAL_100};
    cursor: not-allowed;
  }
`

const checkboxContainerStyles = css`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
`

const checkboxCheckedContainerStyles = css`
  transform: scale(1.15);
  animation: checkScale 0.25s ease-out;

  @keyframes checkScale {
    0% {
      transform: scale(0.8);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1.15);
    }
  }
`

const confirmationContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 16px;
  text-align: center;
`

const warningIconStyles = css`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${colors.WARNING_300}20;
  display: flex;
  align-items: center;
  justify-content: center;
`

const confirmationHeadingStyles = css`
  ${paragraphMedium}
  ${weightBold}
  color: ${colors.WHITE};
  margin: 0;
`

const confirmationListStyles = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const confirmationListItemStyles = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_50};
`

const confirmationNoteStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_70};
  margin: 0;
`

const regenerateButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.WARNING_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.WARNING_400};
  }
`

/**
 * Sortable wrapper for a division row. Uses dnd-kit's useSortable hook.
 */
const SortableDivisionRow: React.FC<{
  id: string
  isApproved: boolean
  children: React.ReactNode
}> = ({ id, isApproved, children }) => {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isApproved,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  )
}

/**
 * Drag handle that only listens to drag events (doesn't interfere with row click).
 */
const DragHandle: React.FC<{ divisionId: string }> = ({ divisionId }) => {
  const { attributes, listeners } = useSortable({ id: divisionId })

  return (
    <div
      css={dragHandleStyles}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      aria-label='Drag to reorder'
    >
      <GripIcon />
    </div>
  )
}

const GenerateScheduleModal: React.FC<GenerateScheduleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  divisions,
  previewPayload = null,
  isLoadingPreview = false,
}) => {
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([])
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [confirmStep, setConfirmStep] = useState(false)
  const [divisionOrder, setDivisionOrder] = useState<string[]>([])

  // Pre-select divisions with status 'generated' or 'approved' when modal opens
  useEffect(() => {
    if (isOpen) {
      const preSelected = divisions
        .filter(
          (d) =>
            !d.isDisabled &&
            !d.isApproved &&
            (d.scheduleStatus === 'generated' ||
              d.scheduleStatus === 'approved')
        )
        .map((d) => d.id)
      setSelectedDivisions(preSelected)
      // Initialize division order from props order
      setDivisionOrder(divisions.map((d) => d.id))
    }
  }, [isOpen, divisions])

  // Divisions sorted by user-specified priority order
  const orderedDivisions = useMemo(() => {
    if (divisionOrder.length === 0) {
      return divisions
    }
    const orderMap = new Map(divisionOrder.map((id, idx) => [id, idx]))
    return [...divisions].sort((a, b) => {
      const aIdx = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const bIdx = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return aIdx - bIdx
    })
  }, [divisions, divisionOrder])

  // Drag-and-drop state
  const [activeDragId, setActiveDragId] = useState<UniqueIdentifier | null>(
    null
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const sortableIds = useMemo(
    () => orderedDivisions.map((d) => d.id),
    [orderedDivisions]
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveDragId(null)

      if (!over || active.id === over.id) {
        return
      }

      setDivisionOrder((prev) => {
        const order = prev.length > 0 ? [...prev] : divisions.map((d) => d.id)
        const oldIndex = order.indexOf(String(active.id))
        const newIndex = order.indexOf(String(over.id))
        if (oldIndex === -1 || newIndex === -1) {
          return prev
        }
        return arrayMove(order, oldIndex, newIndex)
      })
    },
    [divisions]
  )

  const activeDragDivision = useMemo(
    () =>
      activeDragId
        ? orderedDivisions.find((d) => d.id === String(activeDragId))
        : null,
    [activeDragId, orderedDivisions]
  )

  const divisionsWithGames = useMemo(() => {
    const filtered = divisions.filter(
      (d) =>
        selectedDivisions.includes(d.id) &&
        (d.scheduleStatus === 'generated' || d.scheduleStatus === 'approved')
    )
    if (divisionOrder.length === 0) {
      return filtered
    }
    const orderMap = new Map(divisionOrder.map((id, idx) => [id, idx]))
    return [...filtered].sort(
      (a, b) =>
        (orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    )
  }, [divisions, selectedDivisions, divisionOrder])

  const resetForm = useCallback(() => {
    setSelectedDivisions([])
    setSelectedLocations([])
    setConfirmStep(false)
    setDivisionOrder([])
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleConfirm = useCallback(() => {
    if (divisionsWithGames.length > 0 && !confirmStep) {
      setConfirmStep(true)
      return
    }

    // Pass division priority filtered to selected divisions, in priority order
    const fullOrder =
      divisionOrder.length > 0 ? divisionOrder : divisions.map((d) => d.id)
    const priority = fullOrder.filter((id) => selectedDivisions.includes(id))
    onConfirm(selectedDivisions, selectedLocations, priority)
    handleClose()
  }, [
    onConfirm,
    selectedDivisions,
    selectedLocations,
    handleClose,
    divisionsWithGames,
    confirmStep,
    divisionOrder,
    divisions,
  ])

  const toggleDivisionSelection = useCallback(
    (divisionId: string, division: Division) => {
      if (division.isDisabled || division.isApproved) {
        return
      }

      setSelectedDivisions((prev) => {
        const isSelected = prev.includes(divisionId)
        if (isSelected) {
          // Deselect division and all its locations
          const divisionLocations =
            division.locations !== 'all'
              ? division.locations.map((loc) => loc.id)
              : []
          setSelectedLocations((prevLocs) =>
            prevLocs.filter((loc) => !divisionLocations.includes(loc))
          )
          return prev.filter((id) => id !== divisionId)
        } else {
          // Select division and all its locations
          if (division.locations !== 'all') {
            const locationIds = division.locations.map((loc) => loc.id)
            setSelectedLocations((prevLocs) => [
              ...new Set([...prevLocs, ...locationIds]),
            ])
          }
          return [...prev, divisionId]
        }
      })
    },
    []
  )

  const getLocationDisplayText = (division: Division): string => {
    if (division.locations === 'all') {
      return 'All Locations'
    }
    const count = division.locations.length
    return `${count} Location${count !== 1 ? 's' : ''}`
  }

  const isDivisionSelected = (divisionId: string): boolean => {
    return selectedDivisions.includes(divisionId)
  }

  const selectableDivisions = divisions.filter(
    (d) => !d.isDisabled && !d.isApproved
  )
  const allSelected =
    selectableDivisions.length > 0 &&
    selectableDivisions.every((d) => selectedDivisions.includes(d.id))

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      // Deselect all
      setSelectedDivisions([])
      setSelectedLocations([])
    } else {
      // Select all selectable divisions and their locations
      const ids = selectableDivisions.map((d) => d.id)
      const locIds = selectableDivisions.flatMap((d) =>
        d.locations !== 'all' ? d.locations.map((l) => l.id) : []
      )
      setSelectedDivisions(ids)
      setSelectedLocations(locIds)
    }
  }, [allSelected, selectableDivisions])

  const canConfirm = selectedDivisions.length > 0

  const handleCopyToClipboard = useCallback(() => {
    if (!previewPayload) {
      return
    }
    navigator.clipboard.writeText(JSON.stringify(previewPayload, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [previewPayload])

  const showPreview = previewPayload !== null || isLoadingPreview

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      destroyOnClose
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div css={headerStyles}>
        <h2 css={titleStyles}>
          {showPreview
            ? 'Payload Preview'
            : confirmStep && !showPreview
            ? 'Confirm Regeneration'
            : 'Generate Schedule'}
        </h2>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={handleClose}
          aria-label='Close modal'
        >
          <CloseOutlined />
        </button>
      </div>

      {showPreview ? (
        <>
          {/* Preview Content */}
          <div css={formStyles}>
            {isLoadingPreview ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 48px;
                  color: ${colors.NEUTRAL_50};
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                `}
              >
                Loading payload preview...
              </div>
            ) : (
              <pre
                css={css`
                  background-color: ${colors.SECONDARY_500};
                  color: ${colors.NEUTRAL_50};
                  font-family: 'Courier New', Courier, monospace;
                  font-size: 12px;
                  padding: 16px;
                  border-radius: 8px;
                  max-height: calc(90vh - 130px);
                  overflow-y: auto;
                  white-space: pre-wrap;
                  word-break: break-word;
                  margin: 0;
                `}
              >
                {JSON.stringify(previewPayload, null, 2)}
              </pre>
            )}
          </div>

          {/* Preview Footer */}
          <div css={footerStyles}>
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <button
                type='button'
                css={cancelButtonStyles}
                onClick={handleClose}
              >
                CLOSE
              </button>
              <button
                type='button'
                css={css`
                  ${confirmButtonStyles}
                  min-width: 180px;
                `}
                onClick={handleCopyToClipboard}
                disabled={isLoadingPreview}
              >
                {copied ? 'COPIED!' : 'COPY TO CLIPBOARD'}
              </button>
            </div>
          </div>
        </>
      ) : confirmStep ? (
        <>
          {/* Confirmation Content */}
          <div css={confirmationContainerStyles}>
            <div css={warningIconStyles}>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'
                  fill={colors.WARNING_300}
                />
              </svg>
            </div>
            <p css={confirmationHeadingStyles}>
              This will replace existing games
            </p>
            <ul css={confirmationListStyles}>
              {divisionsWithGames.map((d) => (
                <li key={d.id} css={confirmationListItemStyles}>
                  {d.name}
                </li>
              ))}
            </ul>
            <p css={confirmationNoteStyles}>This action cannot be undone.</p>
          </div>

          {/* Confirmation Footer */}
          <div css={footerStyles}>
            <div css={buttonContainerStyles}>
              <button
                type='button'
                css={cancelButtonStyles}
                onClick={() => setConfirmStep(false)}
              >
                GO BACK
              </button>
              <button
                type='button'
                css={regenerateButtonStyles}
                onClick={() => {
                  const fullOrder =
                    divisionOrder.length > 0
                      ? divisionOrder
                      : divisions.map((d) => d.id)
                  const priority = fullOrder.filter((id) =>
                    selectedDivisions.includes(id)
                  )
                  onConfirm(selectedDivisions, selectedLocations, priority)
                  handleClose()
                }}
              >
                REGENERATE
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Form / Content */}
          <div css={formStyles}>
            {/* Section Header */}
            <div css={sectionHeaderStyles}>
              <p css={sectionTitleStyles}>Divisions</p>
              <p css={sectionSubtitleStyles}>
                Select divisions and set solve priority (1 = solved first)
              </p>
            </div>

            {/* Table */}
            <div css={tableContainerStyles}>
              {/* Table Header */}
              <div
                css={[
                  tableHeaderStyles,
                  css`
                    cursor: pointer;
                    &:hover {
                      background-color: ${colors.SECONDARY_200};
                    }
                  `,
                ]}
                onClick={toggleSelectAll}
                role='checkbox'
                aria-checked={allSelected}
                aria-label='Select all divisions'
              >
                <div css={priorityColumnStyles}>
                  <span
                    css={css`
                      ${paragraphXSmall}
                      ${weightMedium}
                      color: ${colors.NEUTRAL_70};
                      text-transform: uppercase;
                      font-size: 10px;
                    `}
                  >
                    Priority
                  </span>
                </div>
                <div css={checkboxContainerStyles}>
                  {allSelected ? (
                    <CheckboxCheckedIcon />
                  ) : (
                    <CheckboxOutlineIcon />
                  )}
                </div>
                <span css={tableHeaderCellStyles}>Divisions</span>
                <span css={tableHeaderCellStyles}>Locations</span>
                <span css={tableHeaderCellStyles}>Status</span>
              </div>

              {/* Division Rows (drag-and-drop sortable) */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableIds}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedDivisions.map((division, index) => {
                    const isSelected = isDivisionSelected(division.id)
                    const isAltRow = index % 2 === 1

                    return (
                      <SortableDivisionRow
                        key={division.id}
                        id={division.id}
                        isApproved={!!division.isApproved}
                      >
                        {/* Division Row */}
                        <div
                          css={[
                            divisionRowStyles,
                            (division.isDisabled || division.isApproved) &&
                              divisionRowDisabledStyles,
                            isAltRow &&
                              !(division.isDisabled || division.isApproved) &&
                              !isSelected &&
                              altRowStyles,
                            isSelected && divisionRowSelectedStyles,
                          ]}
                          onClick={() => {
                            if (!division.isDisabled && !division.isApproved) {
                              toggleDivisionSelection(division.id, division)
                            }
                          }}
                          role='checkbox'
                          aria-checked={isSelected}
                          tabIndex={
                            division.isDisabled || division.isApproved ? -1 : 0
                          }
                          onKeyDown={(e) => {
                            if (
                              (e.key === 'Enter' || e.key === ' ') &&
                              !division.isDisabled &&
                              !division.isApproved
                            ) {
                              e.preventDefault()
                              toggleDivisionSelection(division.id, division)
                            }
                          }}
                        >
                          {/* Drag handle + priority badge */}
                          <div css={priorityColumnStyles}>
                            <DragHandle divisionId={division.id} />
                            <span css={priorityBadgeStyles}>{index + 1}</span>
                          </div>
                          <div
                            css={[
                              checkboxContainerStyles,
                              isSelected && checkboxCheckedContainerStyles,
                            ]}
                          >
                            {division.isDisabled || division.isApproved ? (
                              <CheckboxDisabledIcon />
                            ) : isSelected ? (
                              <CheckboxCheckedIcon />
                            ) : (
                              <CheckboxOutlineIcon />
                            )}
                          </div>
                          <span css={divisionNameStyles}>{division.name}</span>
                          <span css={locationCellStyles}>
                            {getLocationDisplayText(division)}
                          </span>
                          <span css={statusCellStyles}>
                            {division.scheduleStatus || division.isApproved ? (
                              <StatusBadge
                                status={division.scheduleStatus}
                                isApproved={division.isApproved}
                              />
                            ) : (
                              division.status
                            )}
                          </span>
                        </div>
                      </SortableDivisionRow>
                    )
                  })}
                </SortableContext>

                <DragOverlay>
                  {activeDragDivision ? (
                    <div
                      css={css`
                        background: ${colors.SECONDARY_300};
                        padding: 8px 16px;
                        border-radius: 6px;
                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        border: 1px solid ${colors.PRIMARY_300}40;
                      `}
                    >
                      <GripIcon color={colors.NEUTRAL_50} />
                      <span
                        css={css`
                          ${paragraphSmall}
                          ${weightMedium}
                          color: ${colors.WHITE};
                        `}
                      >
                        {activeDragDivision.name}
                      </span>
                      {activeDragDivision.scheduleStatus && (
                        <StatusBadge
                          status={activeDragDivision.scheduleStatus}
                          isApproved={activeDragDivision.isApproved}
                        />
                      )}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>

          {/* Footer */}
          <div css={footerStyles}>
            <div css={buttonContainerStyles}>
              <button
                type='button'
                css={cancelButtonStyles}
                onClick={handleClose}
              >
                CANCEL
              </button>
              <button
                type='button'
                css={confirmButtonStyles}
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                CONFIRM
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default GenerateScheduleModal
